import { useEffect, useState } from 'react';
import { api } from '../api';
import { useTripBuilder } from '../TripBuilderContext';
import { buildDailyPlan, collectPlanPicks } from '../dailyPlanner';

// Surfaced on the Activities step. Pulls the full catalog of activities +
// restaurants for the stop, builds a structured day-by-day plan (anchor +
// support + meals), and offers a one-click "Apply this plan" that toggles
// every suggested item into the user's picks.
//
// For multi-city trips, shows one collapsible plan per stop.
export default function DailyPlanSuggester() {
  const tb = useTripBuilder();

  // Per-stop loaded catalog — hooks must run unconditionally
  const [catalogs, setCatalogs] = useState({}); // { [stopIdx]: { activities, restaurants } }
  const [openStop, setOpenStop] = useState(0);
  const [applyMsg, setApplyMsg] = useState(null);

  useEffect(() => {
    if (!tb.destination) return;
    const stops = tb.stops;
    stops.forEach(async (s, idx) => {
      if (catalogs[idx]) return; // already loaded
      try {
        const [acts, rests] = await Promise.all([
          api.getActivities(s.destination.id),
          api.getRestaurants(s.destination.id),
        ]);
        // Filter by sub-city if this is a bundled stop
        const subName = s.destination._bundleSubName || (
          (s.destination.city || '').includes(' + ')
            ? s.destination.city.split(' + ')[0].trim()
            : null
        );
        let filteredA = acts, filteredR = rests;
        if (subName) {
          const re = new RegExp(`\\b${subName.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}\\b`, 'i');
          const fa = acts.filter(a => re.test(a.name || '') || re.test(a.description || '') || re.test(a.tip || '') || re.test(a.booking_tip || ''));
          const fr = rests.filter(r => re.test(r.name || '') || re.test(r.neighborhood || '') || re.test(r.description || ''));
          if (fa.length > 0) filteredA = fa;
          if (fr.length > 0) filteredR = fr;
        }
        setCatalogs(prev => ({ ...prev, [idx]: { activities: filteredA, restaurants: filteredR } }));
      } catch {
        setCatalogs(prev => ({ ...prev, [idx]: { activities: [], restaurants: [] } }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tb.stops.length, tb.destination?.id]);

  if (!tb.destination) return null;

  function applyPlan(planDays, stopIdx) {
    const { activityIds, restaurantIds } = collectPlanPicks(planDays);
    const stop = tb.stops[stopIdx];
    const isPrimary = stopIdx === 0;
    const cat = catalogs[stopIdx];
    if (!cat) return;

    const currentActIds = new Set((stop.activities || []).map(a => a.id));
    const currentRestIds = new Set((stop.restaurants || []).map(r => r.id));

    let added = 0;
    for (const id of activityIds) {
      const a = cat.activities.find(x => x.id === id);
      if (a && !currentActIds.has(id)) {
        if (isPrimary) tb.toggleActivity(a); else tb.toggleStopActivity(stopIdx - 1, a);
        added++;
      }
    }
    for (const id of restaurantIds) {
      const r = cat.restaurants.find(x => x.id === id);
      if (r && !currentRestIds.has(id)) {
        if (isPrimary) tb.toggleRestaurant(r); else tb.toggleStopRestaurant(stopIdx - 1, r);
        added++;
      }
    }
    setApplyMsg({ stopIdx, count: added });
    setTimeout(() => setApplyMsg(null), 4000);
  }

  return (
    <div className="bg-navy-light border border-teal/25 rounded-2xl p-4 sm:p-5 space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-teal">Suggested daily plan</h3>
          <div className="text-xs text-brand-ink/65 mt-0.5">
            Auto-built from the catalog — anchor sight + secondary activity + dinner per day. Click "Apply" to add all picks.
          </div>
        </div>
        {applyMsg && (
          <div className="text-[11px] font-bold text-teal animate-fade-in">
            ✓ Added {applyMsg.count} to your picks
          </div>
        )}
      </div>

      <div className="space-y-2">
        {tb.stops.map((s, stopIdx) => {
          const cat = catalogs[stopIdx];
          const planDays = cat
            ? buildDailyPlan({
                destination: s.destination,
                nights: s.nights,
                activities: cat.activities,
                restaurants: cat.restaurants,
                isFirstStop: stopIdx === 0,
                isLastStop: stopIdx === tb.stops.length - 1,
              })
            : [];

          return (
            <details
              key={stopIdx}
              open={openStop === stopIdx}
              onToggle={(e) => e.target.open && setOpenStop(stopIdx)}
              className="bg-brand-ink/5 border border-brand-ink/10 rounded-xl overflow-hidden"
            >
              <summary className="cursor-pointer p-3 flex items-center justify-between gap-2 list-none hover:bg-brand-ink/[0.08] transition">
                <div className="font-bold text-sm text-brand-ink">
                  {s.destination.city} <span className="text-brand-ink/55 font-normal">· {s.nights} night{s.nights === 1 ? '' : 's'} · {planDays.length} day plan</span>
                </div>
                <div className="text-[10px] text-brand-ink/45">{openStop === stopIdx ? 'Hide' : 'Show'}</div>
              </summary>
              <div className="p-3 border-t border-brand-ink/10 space-y-3">
                {!cat && <div className="text-xs text-brand-ink/55">Loading catalog…</div>}
                {cat && planDays.length === 0 && (
                  <div className="text-xs text-brand-ink/55">Not enough catalog data to build a plan for {s.destination.city} yet.</div>
                )}
                {cat && planDays.length > 0 && (
                  <>
                    <button
                      onClick={() => applyPlan(planDays, stopIdx)}
                      className="w-full px-3 py-2 bg-teal text-navy text-sm font-bold rounded-lg hover:bg-teal-dark transition"
                    >
                      Apply this {planDays.length}-day plan to {s.destination.city}
                    </button>
                    <div className="space-y-2">
                      {planDays.map((d, di) => (
                        <PlanDayCard key={di} day={d} stopIdx={stopIdx} />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </details>
          );
        })}
      </div>
    </div>
  );
}

function PlanDayCard({ day }) {
  return (
    <div className="bg-navy-light border border-brand-ink/10 rounded-lg p-2.5">
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="text-xs font-bold text-teal uppercase tracking-wider">Day {day.dayIndex + 1}</div>
        <div className="text-[10px] text-brand-ink/55">{day.label}</div>
      </div>
      <div className="space-y-1.5 text-xs">
        {day.morning && (
          <PlanItem icon="🌅" label="Morning" item={day.morning} />
        )}
        {day.afternoon && (
          <PlanItem icon="🌤️" label="Afternoon" item={day.afternoon} />
        )}
        {day.evening && (
          <PlanItem icon="🌆" label="Evening" item={day.evening} />
        )}
        {day.meals?.lunch && (
          <PlanItem icon="🥐" label="Lunch" item={day.meals.lunch} isMeal />
        )}
        {day.meals?.dinner && (
          <PlanItem icon="🍷" label="Dinner" item={day.meals.dinner} isMeal />
        )}
        {day.notes.length > 0 && (
          <div className="text-[10px] text-brand-ink/65 italic pt-1 leading-snug">{day.notes.join(' ')}</div>
        )}
      </div>
    </div>
  );
}

function PlanItem({ icon, label, item, isMeal }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-sm shrink-0">{icon}</span>
      <span className="text-[10px] font-bold text-brand-ink/55 uppercase shrink-0 w-16">{label}</span>
      <span className="font-medium text-brand-ink">{item.name}</span>
      <span className="text-[10px] text-brand-ink/55 ml-auto shrink-0">
        {isMeal ? `${item.price_range || ''} · $${item.avg_meal_for_two || ''}` : (item.duration || '')}
      </span>
    </div>
  );
}
